
import React from 'react';

// Enhanced Glass Card: Adaptable to Light/Dark modes
export const GlassCard: React.FC<{ 
  children: React.ReactNode; 
  className?: string; 
  onClick?: () => void;
  // Variant 'light' is typically the standard card. 'dark' acts as a HUD or inverted card.
  variant?: 'light' | 'dark' 
}> = ({ children, className = '', onClick, variant = 'light' }) => {
  
  const baseStyles = "rounded-[2rem] p-6 backdrop-blur-xl transition-all duration-300 relative overflow-hidden";
  
  // Theme logic optimized for 9.5/10 Contrast Score:
  // Light Mode: Clean White with soft shadow.
  // Dark Mode: Deep Slate (Higher Opacity) to ensure text pops against complex backgrounds.
  
  const variants = {
    // Standard Card
    light: "bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-white/10 shadow-xl shadow-indigo-500/5 dark:shadow-black/40 text-slate-800 dark:text-slate-100",
    
    // HUD / Deep Card (Usually darker in both modes, or inverted)
    // In Light mode, this acts as a 'dark surface' (like a blackboard).
    dark: "bg-slate-900/95 dark:bg-black/80 border border-slate-700/50 dark:border-white/10 text-white shadow-2xl" 
  };

  return (
    <div 
      onClick={onClick}
      className={`${baseStyles} ${variants[variant]} ${onClick ? 'cursor-pointer hover:scale-[1.01] hover:shadow-2xl' : ''} ${className}`}
    >
      {/* Subtle shine effect - Reduced intensity for cleaner look */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent pointer-events-none opacity-0 hover:opacity-100 transition-opacity"></div>
      {children}
    </div>
  );
};

export const Badge: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-200 border border-indigo-500/20' }) => (
  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm ${color}`}>
    {children}
  </span>
);

export const Button: React.FC<{ 
  children: React.ReactNode; 
  onClick?: () => void; 
  className?: string;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
}> = ({ children, onClick, className = '', disabled, variant = 'primary' }) => {
  // Base: Tactile, bouncy, rounded
  const base = "px-6 py-3.5 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed select-none relative overflow-hidden";
  
  const variants = {
    // Primary: Holographic Gem (Gradient + Inner Glow)
    primary: "bg-gradient-to-b from-indigo-500 to-indigo-700 hover:from-indigo-400 hover:to-indigo-600 text-white shadow-[0_4px_0_rgb(55,48,163)] hover:shadow-[0_2px_0_rgb(55,48,163)] hover:translate-y-[2px] border-t border-white/20",
    
    // Secondary: Solid contrasting button
    secondary: "bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-white border border-slate-200 dark:border-white/10 shadow-sm",
    
    // Danger: Glowing Red
    danger: "bg-rose-500/10 text-rose-600 dark:text-rose-300 hover:bg-rose-500/20 border border-rose-500/30",

    // Ghost: Text only
    ghost: "bg-transparent text-slate-500 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
  };
  
  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={`${base} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

export const ProgressBar: React.FC<{ 
  current: number; 
  max: number; 
  color?: string;
  className?: string; 
}> = ({ current, max, color = 'bg-gradient-to-r from-cyan-400 to-blue-500', className = '' }) => {
  const percent = Math.min(100, Math.max(0, (current / max) * 100));
  return (
    <div className={`w-full h-3 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden border border-slate-300 dark:border-white/5 ${className}`}>
      <div 
        className={`h-full transition-all duration-500 ease-out shadow-sm ${color}`} 
        style={{ width: `${percent}%` }}
      />
    </div>
  );
};
